import QueryString from "query-string";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Button,
  ButtonGroup,
  Col,
  Container,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Input,
  InputGroup,
  InputGroupText,
  Row,
  UncontrolledDropdown,
} from "reactstrap";
import {
  AUTOMATION_LOADING,
  BOARD_CARD_LOADING,
  CONTACTS_EXPORT_LOADING,
  CONTACTS_IMPORT_LOADING,
  CONTACTS_LOADING,
  OPENAI_GET_TEMPLATES_LOADING,
} from "../../constants/loading.constant";
import {
  CONTACT_EXPAND_CREATION_FORM,
  CONTACT_EXPAND_EDIT_FORM,
  CONTACT_IMPORT_EXPORT,
  CONTACT_TAGS_FORM,
  CONTACT_CONFIG_FORM,
  CONTACT_DELETE_MODAL,
  AUTOMATION_CREATE_FORM,
  AUTOMATION_EDIT_FORM,
  DELETE_AUTOMATION_MODAL,
  CONTACT_STATES_FORM,
  BOARD_CARD_MODAL,
  CONTACT_MASS_ASSIGNMENT_MODAL,
  CONTACT_FILTER_MODAL,
  OPENAI_SOCIAL_FORM,
} from "../../constants/modal.constant";
import { useContactCore } from "../../cores/contacts";
import { useConfigCore } from "../../cores/config";
import { useGlobalCore } from "../../cores/globals";
import { useMailchimpCore } from "../../cores/mailchimp";
import { useMeCore } from "../../cores/me";
import { ContactRow } from "../component/contacts/ContactRow";
import { ContactsBar } from "../component/contacts/ContactsBar";
import ImportContacts from "../component/contacts/modal/ImportContacts";
import { UpgradePlanButton } from "../component/plan/UpgradePlanButton";
import { MailchimpIcon } from "../component/social/Mailchimp/MailchimpIcon";
import { UpdateLoadingCard } from "../component/UpdateLoadingCard";
import Content from "../layout/Content";
import { Header2 } from "../layout/Header2";
import Layout from "../layout/Layout";
import SideModal from "../layout/SideModal";
import { useIntl } from "react-intl";
import { useRouterCore } from "../../cores/router";
import { CSVLink } from "react-csv";
import moment from "moment";
import GmailButton from "../component/gmail/GmailButton";
import { CheckboxWembii } from "../component/CheckboxWembii";
import { useLocation } from "react-router-dom";
import ContactTags from "../component/contacts/modal/ContactTags";
import Config from "../component/contacts/modal/Config";
import {
  AUTOMATION_ACTION,
  AUTOMATION_CONDITION,
  CONTACT_TAGS,
  CUSTOM_FIELD,
  CONTACT_STATES,
  CARD_PRODUCT,
} from "../../constants/internalModal.constant";
import AreYouSure from "../component/AreYouSure";
import TaskSideModals from "../component/tasks/TaskSideModals";
import CompanySideModals from "../component/contactCompanies/CompanySideModals";
import BoardSideModals from "../component/boards/BoardSideModals";
import EventSideModals from "../component/event/EventSideModals";
import EmailSideModals from "../component/gmail/EmailSideModals";
import ContactSideModals from "../component/contacts/ContactSideModals";
import { Position } from "../../cores/globals/interfaces/globalEntity";
import CreateAutomationForm from "../component/automations/modals/CreateAutomationForm";
import EditAutomationForm from "../component/automations/modals/EditAutomationForm";
import { AutomationTrigger } from "../../cores/automations/enum/AutomationTrigger.enum";
import { ContactSource } from "../../cores/contacts/enum/ContactSource.enum";
import CreateContactForm from "../component/contacts/modal/CreateContactForm";
import EditContactForm from "../component/contacts/modal/EditContactForm";
import { utils, writeFileXLSX } from "xlsx";
import { resolveContactStateLabelId } from "../../cores/contacts/interfaces/ContactState";
import ContactStates from "../component/contacts/modal/ContactStates";
import { Errors } from "../../constants/errors";
import { useBoardCore } from "../../cores/boards";
import UserList from "../component/contacts/modal/UserList";
import { LoadingSpinner } from "../component/LoadingSpinner";
import { PaginationWemmbii } from "../component/pagination/Pagination";
import OpenAiForm from "../component/socialPost/modals/OpenAiForm";
import BulkCreateOpportunitiesModal from "../component/contacts/modal/BulkCreateOpportunitiesModal";
import CrmAiSearchTrigger from "../component/boards/modal/CrmAiSearchTrigger";
import ExportPreviewModal, {
  ExportColumn,
  ExportScope,
  mapRowsToSheetData,
} from "../component/export/ExportPreviewModal";
import { Contact as ExportContact } from "../../cores/contacts/interfaces/api/get-contacts-export";
import "./Contacts.scss";

type QuickSearchField = "name__p" | "email__p" | "tel__p" | "reference__p";

const QUICK_SEARCH_OPTIONS: { label: string; value: QuickSearchField }[] = [
  { label: "Nombre", value: "name__p" },
  { label: "Email", value: "email__p" },
  { label: "Telefono", value: "tel__p" },
  { label: "Referencia", value: "reference__p" },
];

export const ContactsScreen = () => {
  const intl = useIntl();
  const {
    loadings,
    isModal,
    getModalVisibility,
    turnOffModal,
    turnOnModal,
    turnOffInternalModal,
    isLoading,
    getModalContent,
  } = useGlobalCore();
  const {
    company,
    user,
    companyRole,
    companyPlan,
    socialSettings,
    userSettings,
    isAdmin,
    isReseller,
  } = useMeCore();
  const {
    contacts,
    total,
    states,
    source: contactSource,
    contactFilters,
    customFields,
    selectedContacts,
    massAssignment,
    replaceAssignment,
    getExportContacts,
    getContactTags,
    getContactStates,
    getCustomFields,
    getContacts,
    getContact,
    deselectAllContacts,
    selectContact,
    setSource,
    deleteContacts,
    getContactConfig,
    setContactFilters,
    cleanAssigments,
    postMassAssignment,
  } = useContactCore();
  const { syncContacts } = useMailchimpCore();
  const { goToSocial, goToFacebookForms, history } = useRouterCore();
  const { getCommentTypes } = useConfigCore();
  const { cleanCard, errorCode, getBusinessBoards } = useBoardCore();
  const { search } = useLocation();
  const [exportData, setExportData] = useState<any[]>([]);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [exportScope, setExportScope] = useState<ExportScope>("all");
  const [previewRows, setPreviewRows] = useState<Record<string, unknown>[]>([]);
  const [quickSearchField, setQuickSearchField] = useState<QuickSearchField>("name__p");
  const [quickSearchText, setQuickSearchText] = useState("");
  const [isBulkCrmModalOpen, setIsBulkCrmModalOpen] = useState(false);
  const latestContactFiltersRef = useRef(contactFilters);
  const filtersBeforeModalRef = useRef<string>("");
  const skipNextFetchRef = useRef(false);
  const openedContactFromUrlRef = useRef<string | null>(null);
  const isFilterModalOpen = isModal(CONTACT_FILTER_MODAL);
  const headers = useMemo(
    () => [
      {
        label: intl.formatMessage({ id: "app.contacts.name" }),
        key: intl.formatMessage({ id: "app.contacts.name" }),
      },
      {
        label: intl.formatMessage({ id: "app.contacts.email" }),
        key: intl.formatMessage({ id: "app.contacts.email" }),
      },
      {
        label: intl.formatMessage({ id: "app.contacts.phone" }),
        key: intl.formatMessage({ id: "app.contacts.phone" }),
      },
      { label: "Whatsapp", key: "Whatsapp" },
      {
        label: intl.formatMessage({ id: "app.contacts.origin" }),
        key: intl.formatMessage({ id: "app.contacts.origin" }),
      },
      {
        label: intl.formatMessage({ id: "app.contacts.state" }),
        key: intl.formatMessage({ id: "app.contacts.state" }),
      },
      {
        label: intl.formatMessage({ id: "app.contacts.createdAt" }),
        key: intl.formatMessage({ id: "app.contacts.createdAt" }),
      },
      {
        label: intl.formatMessage({ id: "app.contacts.companies.title" }),
        key: intl.formatMessage({ id: "app.contacts.companies.title" }),
      },
      ...customFields.map((field) => ({ label: field.name, key: field.name })),
    ],
    [intl, customFields]
  );

  const exportColumns = useMemo<ExportColumn[]>(
    () => [
      {
        key: "name",
        label: intl.formatMessage({ id: "app.contacts.name" }),
      },
      {
        key: "email",
        label: intl.formatMessage({ id: "app.contacts.email" }),
      },
      {
        key: "phone",
        label: intl.formatMessage({ id: "app.contacts.phone" }),
      },
      { key: "whatsapp", label: "Whatsapp" },
      {
        key: "origin",
        label: intl.formatMessage({ id: "app.contacts.origin" }),
      },
      {
        key: "state",
        label: intl.formatMessage({ id: "app.contacts.state" }),
      },
      {
        key: "createdAt",
        label: intl.formatMessage({ id: "app.contacts.createdAt" }),
      },
      {
        key: "companies",
        label: intl.formatMessage({ id: "app.contacts.companies.title" }),
      },
      ...customFields.map((field) => ({
        key: field.name,
        label: field.name,
      })),
      {
        key: "owners",
        label: intl.formatMessage({ id: "app.contacts.owners" }),
      },
    ],
    [intl, customFields]
  );

  const ref = useRef<any>(null);

  useEffect(() => {
    if (
      !companyPlan?.rules.allowDatabase ||
      (!companyRole?.contacts &&
        !companyRole?.owner &&
        !companyRole?.admin &&
        !companyRole?.agent &&
        !isAdmin &&
        !isReseller)
    )
      goToSocial();
  }, [companyPlan, companyRole, isAdmin, isReseller, goToSocial]);

  useEffect(() => {
    const { source } = QueryString.parse(search);
    const isPrivilegedUser =
      isAdmin || isReseller || companyRole?.owner || companyRole?.admin;
    const ownerFilter =
      !isPrivilegedUser && user?.id ? String(user.id) : "";

    const nextFilters: any = {
      page: 1,
      perPage: contactFilters.perPage || 10,
      owner: ownerFilter,
      order: contactFilters.order || "DESC",
      fieldOrder: contactFilters.fieldOrder || "createdAt",
      sources: [],
    };

    if (typeof source === "string" && Object.values(ContactSource).includes(source as ContactSource)) {
      if (source !== contactSource) {
        setSource(source);
      }
      nextFilters.sources = [source];
    }

    setContactFilters(nextFilters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, company?.id, isAdmin, isReseller, companyRole?.owner, companyRole?.admin, user?.id]);

  const openContactById = useCallback(
    (contactId: string) => {
      if (!company?.id) return;

      getContactTags(company.id);
      getCommentTypes(company.id);
      getCustomFields(company.id);
      getContact(company.id, contactId);
      getBusinessBoards(company.id);
      turnOnModal(
        CONTACT_EXPAND_EDIT_FORM,
        <EditContactForm
          onDelete={async () => {
            deleteContacts(company.id, [contactId], () => {
              getContacts(company.id, contactFilters);
              turnOffModal(CONTACT_EXPAND_EDIT_FORM);
            });
          }}
          onUpdate={() => {
            getContacts(company.id, contactFilters);
          }}
        />,
        Position.LEFT
      );
    },
    [
      company,
      contactFilters,
      deleteContacts,
      getBusinessBoards,
      getCommentTypes,
      getContact,
      getContactTags,
      getContacts,
      getCustomFields,
      turnOffModal,
      turnOnModal,
    ]
  );

  useEffect(() => {
    const params = QueryString.parse(search);
    const contactId = params.contactId as string | undefined;

    if (!contactId || !company?.id) return;
    if (openedContactFromUrlRef.current === contactId) return;

    openedContactFromUrlRef.current = contactId;

    const { contactId: _removed, ...rest } = params;
    const nextSearch = QueryString.stringify(rest);
    history.replace({
      pathname: history.location.pathname,
      search: nextSearch ? `?${nextSearch}` : "",
    });

    openContactById(contactId);
  }, [search, company?.id, history, openContactById]);

  const canSyncMailchimp = useMemo(
    () =>
      socialSettings &&
      socialSettings.mailchimp &&
      (companyRole?.owner || companyRole?.admin),
    [socialSettings, companyRole]
  );

  const onClickImportExportContacts = useCallback(() => {
    getContactTags(company?.id!);
    turnOnModal(CONTACT_IMPORT_EXPORT, true);
  }, [turnOnModal, getContactTags, company]);

  const onClickCreateContact = useCallback(() => {
    getContactTags(company?.id!);
    const baseFilters = {
      page: 1,
      perPage: contactFilters.perPage || 10,
      owner: contactFilters.owner || "",
      sources: contactFilters.sources || [],
      state: contactFilters.state,
      order: contactFilters.order || "DESC",
      fieldOrder: contactFilters.fieldOrder || "createdAt",
      creationDate: contactFilters.creationDate || ["", ""],
    };
    turnOnModal(
      CONTACT_EXPAND_CREATION_FORM,
      <CreateContactForm
        source={ContactSource.MANUAL}
        onCreation={() => {
          setQuickSearchText("");
          setContactFilters(baseFilters as any);
          getContacts(company?.id!, baseFilters as any, false);
        }}
      />,
      Position.LEFT
    );
  }, [
    turnOnModal,
    getContactTags,
    company,
    contactFilters.perPage,
    contactFilters.owner,
    contactFilters.sources,
    contactFilters.state,
    contactFilters.order,
    contactFilters.fieldOrder,
    contactFilters.creationDate,
    getContacts,
    setContactFilters,
  ]);

  const onClickConfig = useCallback(() => {
    getCustomFields(company?.id!);
    getContactConfig(company?.id!);
    turnOnModal(CONTACT_CONFIG_FORM, true);
  }, [turnOnModal, getCustomFields, getContactConfig, company]);

  const resolveContactExportState = useCallback(
    (contact: ExportContact) => {
      const labelId = resolveContactStateLabelId(contact.state);
      if (labelId) {
        return intl.formatMessage({ id: labelId });
      }
      if (contact.state != null && contact.state >= 100) {
        return states.find((st) => +st.id === contact.state)?.name || "";
      }
      return "";
    },
    [intl, states]
  );

  const mapContactsToCsvRows = useCallback(
    (contacts: ExportContact[]) =>
      contacts.map((contact) => ({
        [intl.formatMessage({ id: "app.contacts.name" })]: contact.name,
        [intl.formatMessage({ id: "app.contacts.email" })]: contact.email,
        [intl.formatMessage({ id: "app.contacts.phone" })]:
          contact.phoneNumber,
        Whatsapp: contact.whatsapp,
        [intl.formatMessage({ id: "app.contacts.origin" })]: contact.source,
        [intl.formatMessage({ id: "app.contacts.state" })]:
          resolveContactExportState(contact),
        [intl.formatMessage({ id: "app.contacts.createdAt" })]: moment(
          contact.createdAt
        ).format("DD/MM/YYYY"),
        [intl.formatMessage({ id: "app.contacts.companies.title" })]:
          contact.companies,
        ...contact.fields.reduce<Record<string, string>>((prev, curr) => {
          prev[curr.name] = curr.value;
          return prev;
        }, {}),
        [intl.formatMessage({ id: "app.contacts.owners" })]:
          contact.owners.join(","),
      })),
    [intl, resolveContactExportState]
  );

  const mapContactsToPreviewRows = useCallback(
    (contacts: ExportContact[]): Record<string, unknown>[] =>
      contacts.map((contact) => ({
        id: contact.id,
        name: contact.name,
        email: contact.email,
        phone: contact.phoneNumber,
        whatsapp: contact.whatsapp,
        origin: contact.source,
        state: resolveContactExportState(contact),
        createdAt: moment(contact.createdAt).format("DD/MM/YYYY"),
        companies: Array.isArray(contact.companies)
          ? contact.companies.join(", ")
          : contact.companies,
        owners: contact.owners.join(", "),
        ...contact.fields.reduce<Record<string, string>>((prev, curr) => {
          prev[curr.name] = curr.value;
          return prev;
        }, {}),
      })),
    [resolveContactExportState]
  );

  const loadExportPreview = useCallback(
    async (scope: ExportScope) => {
      if (!company?.id) return;
      setPreviewLoading(true);
      try {
        const contacts = await getExportContacts(company.id, contactFilters);
        if (!contacts) {
          setPreviewRows([]);
          return;
        }

        const scopedContacts =
          scope === "selected"
            ? contacts.filter((contact) =>
                selectedContacts.some(
                  (selected) => String(selected.id) === String(contact.id)
                )
              )
            : contacts;

        setPreviewRows(mapContactsToPreviewRows(scopedContacts));
      } finally {
        setPreviewLoading(false);
      }
    },
    [
      company?.id,
      contactFilters,
      getExportContacts,
      mapContactsToPreviewRows,
      selectedContacts,
    ]
  );

  const onClickCSVExport = useCallback(async () => {
    const contacts = await getExportContacts(company?.id!, contactFilters);
    if (contacts) {
      setExportData(mapContactsToCsvRows(contacts));
      setTimeout(() => ref.current?.link?.click(), 0);
    }
  }, [
    company,
    contactFilters,
    getExportContacts,
    mapContactsToCsvRows,
  ]);

  const onClickExcelExport = useCallback(() => {
    const initialScope: ExportScope =
      selectedContacts.length > 0 ? "selected" : "all";
    setExportScope(initialScope);
    setPreviewRows([]);
    setPreviewOpen(true);
    loadExportPreview(initialScope);
  }, [loadExportPreview, selectedContacts.length]);

  const onChangeExportScope = useCallback(
    (scope: ExportScope) => {
      setExportScope(scope);
      loadExportPreview(scope);
    },
    [loadExportPreview]
  );

  const onCloseExportPreview = useCallback(() => {
    setPreviewOpen(false);
    setPreviewLoading(false);
  }, []);

  const onDownloadExcelFromPreview = useCallback(
    (visibleColumns: ExportColumn[]) => {
      const data = mapRowsToSheetData(previewRows, visibleColumns);
      const wb = utils.book_new();
      const ws = utils.json_to_sheet(data);
      utils.book_append_sheet(wb, ws, "Contactos");
      writeFileXLSX(
        wb,
        `Contacts_${company?.id!}_${moment().format("DD-MM-YYYY")}.xlsx`
      );
      setPreviewOpen(false);
    },
    [company?.id, previewRows]
  );

  useEffect(() => {
    getContactTags(company?.id!);
    getContactStates(company?.id!);
    getCustomFields(company?.id!);
    getContactConfig(company?.id!);
  }, [
    getContactTags,
    getCustomFields,
    getContactConfig,
    getContactStates,
    company,
  ]);

  const handleSyncContacts = useCallback(() => {
    syncContacts(company?.id!);
  }, [syncContacts, company]);

  const isPageSelected = useMemo(() => {
    const canSelectContacts =
      isAdmin || companyRole?.owner
        ? contacts
        : contacts.filter(
          (contact) =>
            contact.owners.find((owner) => owner.id === user?.id) ||
            contact.owners.length === 0
        );

    return canSelectContacts.reduce(
      (prev, curr) =>
        prev && !!selectedContacts.find((contact) => contact.id === curr.id),
      true
    );
  }, [contacts, selectedContacts, user?.id, isAdmin, companyRole]);

  const toggleAllContacts = useCallback(() => {
    if (isPageSelected) {
      deselectAllContacts();
    } else {
      const canSelectContacts =
        isAdmin || companyRole?.owner
          ? contacts
          : contacts.filter(
            (addContact) =>
              !selectedContacts.find(
                (selectedContact) => addContact.id === selectedContact.id
              ) &&
              (addContact.owners.find((owner) => owner.id === user?.id) ||
                addContact.owners.length === 0)
          );
      canSelectContacts.forEach((contact) => {
        selectContact(contact);
      });
    }
  }, [
    deselectAllContacts,
    selectContact,
    isAdmin,
    companyRole,
    contacts,
    selectedContacts,
    isPageSelected,
    user?.id,
  ]);

  const deleteSelected = useCallback(() => {
    turnOnModal(
      CONTACT_DELETE_MODAL,
      <AreYouSure
        message={intl.formatMessage({ id: "app.contacts.delete.message" })}
        onSubmit={() => {
          const contacts = selectedContacts.map((contact) => contact.id);
          deleteContacts(company?.id!, contacts, () => {
            deselectAllContacts();
            getContacts(company?.id!, contactFilters);
          });

          turnOffModal(CONTACT_DELETE_MODAL);
        }}
        onClose={() => turnOffModal(CONTACT_DELETE_MODAL)}
      />,
      Position.LEFT
    );
  }, [
    turnOnModal,
    turnOffModal,
    deleteContacts,
    getContacts,
    deselectAllContacts,
    selectedContacts,
    company,
    contactFilters,
    intl,
  ]);

  const createAutomation = useCallback(() => {
    turnOnModal(AUTOMATION_CREATE_FORM, true);
  }, [turnOnModal]);

  const openMassAssignment = useCallback(() => {
    cleanAssigments();
    turnOnModal(CONTACT_MASS_ASSIGNMENT_MODAL, true);
  }, [turnOnModal, cleanAssigments]);

  const closeMassAssignment = useCallback(() => {
    if (selectedContacts.length && massAssignment.length) {
      const contacts = selectedContacts.map((contact) => contact.id);
      postMassAssignment(
        company?.id!,
        {
          contacts,
          users: massAssignment,
          replace: replaceAssignment,
        },
        () => {
          deselectAllContacts();
          getContacts(company?.id!, contactFilters);
        }
      );
    }
    turnOffModal(CONTACT_MASS_ASSIGNMENT_MODAL);
  }, [
    selectedContacts,
    massAssignment,
    replaceAssignment,
    company,
    contactFilters,
    turnOffModal,
    postMassAssignment,
    deselectAllContacts,
    getContacts,
  ]);

  const canExport = useMemo(
    () =>
      companyRole?.owner ||
      companyRole?.admin ||
      companyPlan?.rules.allowExportContact,
    [companyPlan, companyRole]
  );

  const canImport = useMemo(
    () => companyRole?.owner || companyRole?.admin,
    [companyRole]
  );

  const showGmailButton = useMemo(
    () =>
      socialSettings?.gmail ||
      userSettings?.gmail ||
      socialSettings?.smtp ||
      userSettings?.smtp ||
      socialSettings?.office365 ||
      userSettings?.office365,
    [socialSettings, userSettings]
  );
  const changeFilters = useCallback(
    (field) => {
      setContactFilters({ ...contactFilters, ...field });
    },
    [contactFilters, setContactFilters]
  );

  const hasAdvancedFilters = useMemo(() => {
    return !!(
      contactFilters.state ||
      contactFilters.owner ||
      (contactFilters.sources && contactFilters.sources.length > 0) ||
      (contactFilters.creationDate && contactFilters.creationDate.some(Boolean))
    );
  }, [contactFilters]);

  const clearAllFilters = useCallback(() => {
    setQuickSearchText("");
    setContactFilters({
      page: 1,
      perPage: contactFilters.perPage || 10,
      owner: "",
      sources: [],
      state: undefined,
      order: "DESC",
      fieldOrder: "createdAt",
      creationDate: ["", ""],
    });
  }, [contactFilters.perPage, setContactFilters]);

  useEffect(() => {
    latestContactFiltersRef.current = contactFilters;
  }, [contactFilters]);

  useEffect(() => {
    if (isFilterModalOpen) {
      filtersBeforeModalRef.current = JSON.stringify(latestContactFiltersRef.current || {});
      return;
    }

    if (!filtersBeforeModalRef.current) {
      return;
    }

    const currentSerialized = JSON.stringify(latestContactFiltersRef.current || {});
    if (currentSerialized === filtersBeforeModalRef.current) {
      skipNextFetchRef.current = true;
    }
    filtersBeforeModalRef.current = "";
  }, [isFilterModalOpen]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      const currentFilters = latestContactFiltersRef.current as any;
      const searchText = quickSearchText.trim();
      const {
        name__p: _name,
        email__p: _email,
        tel__p: _tel,
        reference__p: _reference,
        ...restFilters
      } = currentFilters;

      const nextFilters: any = {
        ...restFilters,
        page: 1,
      };

      if (searchText) {
        nextFilters[quickSearchField] = searchText;
      }

      const currentQuick = {
        name__p: String(currentFilters?.name__p || "").trim(),
        email__p: String(currentFilters?.email__p || "").trim(),
        tel__p: String(currentFilters?.tel__p || "").trim(),
        reference__p: String(currentFilters?.reference__p || "").trim(),
      };
      const nextQuick = {
        name__p: quickSearchField === "name__p" ? searchText : "",
        email__p: quickSearchField === "email__p" ? searchText : "",
        tel__p: quickSearchField === "tel__p" ? searchText : "",
        reference__p: quickSearchField === "reference__p" ? searchText : "",
      };

      if (
        currentQuick.name__p === nextQuick.name__p &&
        currentQuick.email__p === nextQuick.email__p &&
        currentQuick.tel__p === nextQuick.tel__p &&
        currentQuick.reference__p === nextQuick.reference__p &&
        currentFilters.page === 1
      ) {
        return;
      }

      setContactFilters(nextFilters);
    }, 300);

    return () => clearTimeout(timeout);
  }, [quickSearchText, quickSearchField, setContactFilters]);

  useEffect(() => {
    if (!isFilterModalOpen) {
      if (skipNextFetchRef.current) {
        skipNextFetchRef.current = false;
        return;
      }
      getContacts(company!.id, contactFilters);
    }
  }, [getContacts, company, contactFilters, isFilterModalOpen]);

  return (
    <Layout>
      <Header2
        title={`${intl.formatMessage({ id: "app.contacts.title" })} (${total})`}
        rightChildren={
          <>
            <PaginationWemmbii
              className="d-none d-md-flex"
              hideTitle
              page={contactFilters.page}
              setPage={(page) => changeFilters({ page })}
              perPage={contactFilters.perPage}
              setPerPage={(perPage) => changeFilters({ perPage })}
              disabledPages={contacts.length < contactFilters.perPage}
            />
            {showGmailButton && (
              <GmailButton
                contacts={selectedContacts}
                classname="border-0 mr-2"
              />
            )}

            {canSyncMailchimp && (
              <Button
                type="button"
                className="mailchimpButton mr-2"
                onClick={handleSyncContacts}
              >
                <MailchimpIcon active={true} className="mr-1" />
                {intl.formatMessage({ id: "app.contacts.mailchimp.sync" })}
              </Button>
            )}
            {!canExport ? (
              <UpgradePlanButton />
            ) : (
              <div className="d-none">
                <CSVLink
                  className="d-none "
                  headers={headers}
                  filename={`Contacts_${company?.id!}_${moment().format(
                    "DD-MM-YYYY"
                  )}.csv`}
                  data={exportData}
                  ref={ref}
                />
                <ButtonGroup className="mx-2">
                  <UncontrolledDropdown>
                    <DropdownToggle
                      type="button"
                      caret
                      className="excelButton d-flex align-items-center"
                    >
                      <LoadingSpinner id={CONTACTS_EXPORT_LOADING} />
                      {intl.formatMessage({ id: "app.contacts.export" })}
                    </DropdownToggle>
                    <DropdownMenu>
                      <DropdownItem onClick={onClickCSVExport}>
                        <FontAwesomeIcon
                          icon={["fas", "file-csv"]}
                          size="1x"
                          className="mr-2"
                        />
                        CSV
                      </DropdownItem>
                      <DropdownItem onClick={onClickExcelExport}>
                        <FontAwesomeIcon
                          icon={["fas", "file-excel"]}
                          size="1x"
                          className="mr-2"
                        />
                        Excel
                      </DropdownItem>
                    </DropdownMenu>
                  </UncontrolledDropdown>
                </ButtonGroup>
              </div>
            )}
            <Button type="button" className="whiteButton " onClick={onClickCreateContact}>
              {intl.formatMessage({ id: "app.contacts.createContact" })}
            </Button>
            <UncontrolledDropdown className="ml-2 contacts-toolbar__config-dropdown">
              <DropdownToggle
                type="button"
                className="whiteButton contacts-toolbar__icon-btn"
                caret={false}
              >
                <FontAwesomeIcon icon={["fas", "gear"]} />
              </DropdownToggle>
              <DropdownMenu right className="contacts-toolbar__menu">
                <DropdownItem header>Configuracion</DropdownItem>
                {canExport ? (
                  <>
                    <DropdownItem onClick={onClickCSVExport}>
                      <FontAwesomeIcon icon={["fas", "file-csv"]} className="mr-2" />
                      Exportar CSV
                    </DropdownItem>
                    <DropdownItem onClick={onClickExcelExport}>
                      <FontAwesomeIcon icon={["fas", "file-excel"]} className="mr-2" />
                      Exportar Excel
                    </DropdownItem>
                  </>
                ) : (
                  <DropdownItem disabled>Exportar (requiere plan)</DropdownItem>
                )}
                {canImport && (
                  <DropdownItem onClick={onClickImportExportContacts}>
                    <FontAwesomeIcon icon={["fas", "file-import"]} className="mr-2" />
                    Importar contactos
                  </DropdownItem>
                )}
                <DropdownItem divider />
                <DropdownItem onClick={onClickConfig}>
                  <FontAwesomeIcon icon={["fas", "sliders-h"]} className="mr-2" />
                  Configurar campos y estados
                </DropdownItem>
              </DropdownMenu>
            </UncontrolledDropdown>
            <Button
              type="button"
              className="whiteButton ml-2 contacts-toolbar__icon-btn"
              onClick={() =>
                turnOnModal(CONTACT_FILTER_MODAL, true, Position.RIGHT)
              }
            >
              <FontAwesomeIcon icon={["fas", "filter"]} />
            </Button>
          </>
        }
      />
      <Content>
        <Container fluid className="contacts-page">
          <Row>
            <Col xs="12">
              <UpdateLoadingCard
                loadingId={CONTACTS_LOADING}
                text={intl.formatMessage({
                  id: "app.contacts.updatingContacts",
                })}
              />
              {!isLoading(CONTACTS_LOADING) && (
                <Container fluid className="contacts-page__content px-0">
                  <div className="contacts-ai-search mb-3">
                    <div className="contacts-ai-search__head">
                      <span className="contacts-ai-search__title">
                        Búsqueda de contactos
                      </span>
                      <div className="contacts-ai-search__head-actions">
                        {hasAdvancedFilters && (
                          <Button
                            type="button"
                            color="secondary"
                            outline
                            size="sm"
                            className="contacts-ai-search__clear"
                            onClick={clearAllFilters}
                          >
                            Limpiar
                          </Button>
                        )}
                        <Button
                          type="button"
                          color="secondary"
                          outline
                          size="sm"
                          className="contacts-ai-search__advanced"
                          onClick={() =>
                            turnOnModal(CONTACT_FILTER_MODAL, true, Position.RIGHT)
                          }
                        >
                          <FontAwesomeIcon
                            icon={["fas", "sliders-h"]}
                            className="mr-1"
                          />
                          Filtros
                        </Button>
                      </div>
                    </div>
                    <div className="contacts-ai-search__row">
                      <InputGroup className="contacts-ai-search__group">
                        <Input
                          type="select"
                          className="contacts-ai-search__field"
                          value={quickSearchField}
                          onChange={(e) =>
                            setQuickSearchField(e.target.value as QuickSearchField)
                          }
                        >
                          {QUICK_SEARCH_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </Input>
                        <InputGroupText className="contacts-ai-search__icon">
                          <FontAwesomeIcon icon={["fas", "search"]} />
                        </InputGroupText>
                        <Input
                          type="text"
                          className="contacts-ai-search__input"
                          placeholder="Buscar por nombre, email, teléfono..."
                          value={quickSearchText}
                          onChange={(e) => setQuickSearchText(e.target.value)}
                        />
                      </InputGroup>
                      <CrmAiSearchTrigger focus="contacts" />
                    </div>
                    <div className="contacts-ai-search__meta">
                      <span className="contacts-ai-search__meta-item">
                        <strong>{contacts.length}</strong>
                        <span className="ml-1">visibles</span>
                      </span>
                      <span className="contacts-ai-search__meta-item">
                        <strong>{total}</strong>
                        <span className="ml-1">totales</span>
                      </span>
                      {selectedContacts.length > 0 && (
                        <span className="contacts-ai-search__meta-item is-selected">
                          {selectedContacts.length} seleccionados
                        </span>
                      )}
                      {hasAdvancedFilters && (
                        <span className="contacts-ai-search__meta-item is-active">
                          filtros activos
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="contacts-list-toolbar">
                    <PaginationWemmbii
                      className="d-flex d-md-none"
                      hideTitle
                      page={contactFilters.page}
                      setPage={(page) => changeFilters({ page })}
                      perPage={contactFilters.perPage}
                      setPerPage={(perPage) => changeFilters({ perPage })}
                      disabledPages={contacts.length < contactFilters.perPage}
                    />
                    <div className="contacts-list-toolbar__select">
                      <CheckboxWembii
                        className="my-auto"
                        checked={isPageSelected}
                        label={
                          isPageSelected
                            ? intl.formatMessage({
                                id: "app.contacts.deselect",
                              })
                            : intl.formatMessage({ id: "app.contacts.select" })
                        }
                        onChange={toggleAllContacts}
                      />
                    </div>

                    {selectedContacts.length > 0 && (
                      <div className="contacts-bulk-actions">
                        <button
                          type="button"
                          className="btn contacts-bulk-actions__btn contacts-bulk-actions__btn--crm"
                          onClick={() => setIsBulkCrmModalOpen(true)}
                        >
                          <FontAwesomeIcon
                            icon={["fas", "briefcase"]}
                            size="1x"
                            className="mr-1 my-auto"
                          />
                          <p className="d-none my-auto d-md-flex w-100 mx-2 text-nowrap">
                            Crear oportunidad en CRM
                          </p>
                        </button>
                        <button
                          type="button"
                          className="btn contacts-bulk-actions__btn"
                          onClick={openMassAssignment}
                        >
                          <FontAwesomeIcon
                            icon={["fas", "user"]}
                            size="1x"
                            className="mr-1 my-auto"
                          />
                          <p className="d-none my-auto d-md-flex w-100 mx-2 text-nowrap">
                            {intl.formatMessage({
                              id: "app.contacts.assignment",
                            })}
                          </p>
                        </button>
                        <button
                          type="button"
                          className="btn contacts-bulk-actions__btn"
                          onClick={createAutomation}
                        >
                          <FontAwesomeIcon
                            icon={["fas", "robot"]}
                            size="1x"
                            className="mr-1 my-auto"
                          />
                          <p className="d-none my-auto d-md-flex w-100 mx-2 text-nowrap">
                            {intl.formatMessage({
                              id: "app.contacts.automate.emails",
                            })}
                          </p>
                        </button>
                        <button
                          type="button"
                          className="btn btn-danger contacts-bulk-actions__btn contacts-bulk-actions__btn--danger"
                          onClick={deleteSelected}
                        >
                          <FontAwesomeIcon
                            icon={["fas", "trash"]}
                            size="1x"
                            className="mr-1 my-auto"
                          />
                          <p className="d-none my-auto d-md-flex w-100 mx-2 text-nowrap">
                            {intl.formatMessage({
                              id: "app.contacts.deleteAll",
                            })}
                          </p>
                        </button>
                      </div>
                    )}
                  </div>
                  <div className="contacts-list">
                    {contacts.map((contact) => (
                      <ContactRow key={contact.id} contact={contact} />
                    ))}
                  </div>
                  {!loadings[CONTACTS_LOADING] && contacts.length === 0 && (
                    <div className="contacts-ai-empty">
                      <span className="contacts-ai-empty__badge">Asistente IA Contactos</span>
                      <h4 className="contacts-ai-empty__title">
                        Aún no tienes contactos en tu base.
                      </h4>
                      <p className="contacts-ai-empty__text">
                        Desde este módulo puedes centralizar leads, segmentar por origen y activar
                        automatizaciones comerciales.
                      </p>
                      <div className="contacts-ai-empty__grid">
                        <div className="contacts-ai-empty__card">
                          <h6>Cargar manualmente</h6>
                          <p>Crea contactos uno a uno con datos clave y responsable asignado.</p>
                          <Button type="button" color="primary" onClick={onClickCreateContact}>
                            Crear contacto
                          </Button>
                        </div>
                        <div className="contacts-ai-empty__card">
                          <h6>Importar en lote</h6>
                          <p>Sube CSV/Excel para poblar tu base en minutos y etiquetar origen.</p>
                          <Button
                            type="button"
                            color="secondary"
                            outline
                            onClick={onClickImportExportContacts}
                          >
                            Importar contactos
                          </Button>
                        </div>
                        <div className="contacts-ai-empty__card">
                          <h6>Capturar leads desde formularios</h6>
                          <p>Conecta Facebook Forms y convierte respuestas en oportunidades.</p>
                          <Button
                            type="button"
                            color="secondary"
                            outline
                            onClick={goToFacebookForms}
                          >
                            Ir a formularios
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}
                </Container>
              )}
            </Col>
          </Row>
        </Container>
      </Content>
      <SideModal
        active={isModal(CONTACT_FILTER_MODAL)}
        visible={getModalVisibility(CONTACT_FILTER_MODAL)}
        onClose={() => turnOffModal(CONTACT_FILTER_MODAL)}
      >
        <ContactsBar />
      </SideModal>
      <BulkCreateOpportunitiesModal
        isOpen={isBulkCrmModalOpen}
        contacts={selectedContacts}
        onClose={() => setIsBulkCrmModalOpen(false)}
        onSuccess={deselectAllContacts}
      />
      <SideModal
        active={isModal(CONTACT_IMPORT_EXPORT)}
        visible={getModalVisibility(CONTACT_IMPORT_EXPORT)}
        onClose={() => turnOffModal(CONTACT_IMPORT_EXPORT)}
        showSppiner={isLoading(CONTACTS_IMPORT_LOADING)}
        sppinerMessage="Importando contactos. El proceso puede tardar varios minutos."
      >
        <ImportContacts />
      </SideModal>
      <SideModal
        active={isModal(CONTACT_TAGS_FORM)}
        visible={getModalVisibility(CONTACT_TAGS_FORM)}
        onClose={() => {
          turnOffModal(CONTACT_TAGS_FORM);
          turnOffInternalModal(CONTACT_TAGS);
        }}
      >
        <ContactTags
          onUpdate={() => {
            getContacts(company?.id!, contactFilters, false);
          }}
        />
      </SideModal>
      <SideModal
        active={isModal(CONTACT_STATES_FORM)}
        visible={getModalVisibility(CONTACT_STATES_FORM)}
        onClose={() => {
          turnOffModal(CONTACT_STATES_FORM);
          turnOffInternalModal(CONTACT_STATES);
        }}
      >
        <ContactStates
          onUpdate={() => {
            getContacts(company?.id!, contactFilters, false);
          }}
        />
      </SideModal>
      <SideModal
        active={isModal(CONTACT_CONFIG_FORM)}
        visible={getModalVisibility(CONTACT_CONFIG_FORM)}
        onClose={() => {
          turnOffModal(CONTACT_CONFIG_FORM);
          turnOffInternalModal(CUSTOM_FIELD);
        }}
      >
        <Config />
      </SideModal>
      <SideModal
        active={isModal(AUTOMATION_CREATE_FORM)}
        visible={getModalVisibility(AUTOMATION_CREATE_FORM)}
        onClose={() => turnOffModal(AUTOMATION_CREATE_FORM)}
      >
        <CreateAutomationForm
          automation={AutomationTrigger.CONTACT}
          contacts={selectedContacts}
        />
      </SideModal>
      <SideModal
        active={isModal(AUTOMATION_EDIT_FORM)}
        visible={getModalVisibility(AUTOMATION_EDIT_FORM)}
        showSppiner={isLoading(AUTOMATION_LOADING)}
        onClose={() => {
          turnOffModal(AUTOMATION_EDIT_FORM);
          turnOffInternalModal(AUTOMATION_CONDITION);
          turnOffInternalModal(AUTOMATION_ACTION);
        }}
      >
        <EditAutomationForm />
      </SideModal>
      <SideModal
        active={isModal(DELETE_AUTOMATION_MODAL)}
        visible={getModalVisibility(DELETE_AUTOMATION_MODAL)}
        onClose={() => turnOffModal(DELETE_AUTOMATION_MODAL)}
        left
      >
        {getModalContent(DELETE_AUTOMATION_MODAL)}
      </SideModal>
      <SideModal
        active={isModal(BOARD_CARD_MODAL)}
        visible={getModalVisibility(BOARD_CARD_MODAL)}
        onClose={() => {
          turnOffModal(BOARD_CARD_MODAL);
          turnOffInternalModal(CARD_PRODUCT);
          cleanCard();
        }}
        showSppiner={isLoading(BOARD_CARD_LOADING)}
        showError={errorCode === Errors.CardLoad}
      >
        {getModalContent(BOARD_CARD_MODAL)}
      </SideModal>
      <SideModal
        active={isModal(CONTACT_MASS_ASSIGNMENT_MODAL)}
        visible={getModalVisibility(CONTACT_MASS_ASSIGNMENT_MODAL)}
        onClose={closeMassAssignment}
      >
        <UserList />
      </SideModal>
      <SideModal
        active={isModal(OPENAI_SOCIAL_FORM)}
        visible={getModalVisibility(OPENAI_SOCIAL_FORM)}
        onClose={() => turnOffModal(OPENAI_SOCIAL_FORM)}
        showSppiner={isLoading(OPENAI_GET_TEMPLATES_LOADING)}
      >
        <OpenAiForm emailTemplates />
      </SideModal>
      <TaskSideModals />
      <ContactSideModals />
      <CompanySideModals />
      <BoardSideModals />
      <EventSideModals />
      <EmailSideModals />
      <ExportPreviewModal
        isOpen={previewOpen}
        title={intl.formatMessage({
          id: "app.contacts.export.previewTitle",
        })}
        columns={exportColumns}
        rows={previewRows}
        loading={previewLoading}
        onClose={onCloseExportPreview}
        onDownload={onDownloadExcelFromPreview}
        countMessageId="app.contacts.export.previewCount"
        truncatedMessageId="app.contacts.export.previewTruncated"
        emptyMessageId="app.contacts.export.previewEmpty"
        downloadLabelId="app.contacts.export.downloadExcel"
        scope={{
          value: exportScope,
          selectedCount: selectedContacts.length,
          onChange: onChangeExportScope,
        }}
      />
    </Layout>
  );
};
